<template>
  <ActionPanelShell title="Review request" :subtitle="subtitle" @close="close">
    <div class="sp" data-test="review-panel">
      <div v-if="loading" class="sp-skel" aria-hidden="true" data-test="review-loading"><i></i><i></i><i></i><i></i></div>

      <section v-else-if="loadError" class="sp-sec" data-test="review-load-error">
        <PanelNotice tone="error" icon="fa-exclamation-triangle">
          <strong class="rp-strong">Couldn’t load review status</strong>
          <span class="rp-block">{{ loadError }}</span>
        </PanelNotice>
        <div>
          <button type="button" class="sp-btn-secondary" data-test="review-retry" @click="loadStatus"><i class="fa fa-refresh" aria-hidden="true"></i> Try again</button>
        </div>
      </section>

      <template v-else>
        <!-- Why a request cannot go: the Do not send list, a STOP reply, or the automation skipping. -->
        <section v-if="optedOut || repliedStop || (automationWouldSkip && !optedOut)" class="sp-sec rp-banners">
          <PanelNotice v-if="optedOut" tone="warn" icon="fa-ban" data-test="rr-optout-banner">
            <strong class="rp-strong">On the Do not send list. Review requests are paused.</strong>
            <span class="rp-chips">
              <span class="rp-chip">{{ optOutReasonText }}</span>
              <span>{{ optOutExpiryText }}</span>
              <span v-if="optOut && optOut.optedOutByName">Added by {{ optOut.optedOutByName }}</span>
            </span>
            <span v-if="optOut && optOut.note" class="rp-block rp-quote">“{{ optOut.note }}”</span>
          </PanelNotice>

          <PanelNotice v-if="repliedStop" tone="warn" icon="fa-hand-paper-o" data-test="rr-stop-banner">
            Patient replied STOP — no review requests can be sent.
          </PanelNotice>

          <PanelNotice v-if="automationWouldSkip && !optedOut && !repliedStop" tone="warn" icon="fa-clock-o" data-test="rr-auto-skip">
            Automation is skipping {{ firstName }} for now: {{ automationSkipDetail }}. You can still send one by hand.
          </PanelNotice>
        </section>

        <!-- Send first. -->
        <section class="sp-sec" :class="{ 'is-muted': optedOut }" data-test="review-send">
          <div>
            <div class="sp-h">Send a review request now</div>
            <p class="sp-sub">Goes out right away, even while automatic requests are paused.</p>
          </div>

          <label class="rp-chan" :class="{ 'is-on': useSms && !smsOff, 'is-off': smsOff }">
            <input v-model="useSms" type="checkbox" class="rp-chan__box" :disabled="smsOff" data-test="review-channel-sms">
            <span class="rp-chan__body">
              <span class="rp-chan__title">Text message</span>
              <span class="rp-chan__sub" :class="{ 'is-reason': !channels.smsAvailable }">{{ channels.smsAvailable ? patientPhoneDisplay : smsReasonText }}</span>
            </span>
            <i class="fa fa-comment-o rp-chan__ic" aria-hidden="true"></i>
          </label>

          <label class="rp-chan" :class="{ 'is-on': useEmail && !emailOff, 'is-off': emailOff }">
            <input v-model="useEmail" type="checkbox" class="rp-chan__box" :disabled="emailOff" data-test="review-channel-email">
            <span class="rp-chan__body">
              <span class="rp-chan__title">Email</span>
              <span class="rp-chan__sub" :class="{ 'is-reason': !channels.emailAvailable }">{{ channels.emailAvailable ? (patientData && patientData.Email) : emailReasonText }}</span>
            </span>
            <i class="fa fa-envelope-o rp-chan__ic" aria-hidden="true"></i>
          </label>

          <PanelNotice v-if="recentWithin7Days && !optedOut && !sendHardBlocked" tone="warn" icon="fa-clock-o" data-test="review-recent">
            A request already went out{{ recentRequestAt ? ` on ${formatWhen(recentRequestAt)}` : ' recently' }}. You’ll be asked to confirm before sending again.
          </PanelNotice>
        </section>

        <!-- Automatic requests: turning them off asks why, right here. -->
        <section class="sp-sec" data-test="review-auto">
          <div class="rp-auto">
            <div class="rp-auto__text">
              <div class="sp-h">Automatic requests</div>
              <p class="sp-sub">Turning this off stops every review request to {{ firstName }}, automatic and by hand.</p>
            </div>
            <label class="rp-switch" :title="switchOn ? 'Requests on' : 'Requests paused'">
              <input
                type="checkbox"
                role="switch"
                class="rp-switch__input"
                :checked="switchOn"
                :disabled="optToggling || dnsSaving"
                :aria-label="`Automatic review requests ${switchOn ? 'on' : 'paused'}`"
                data-test="review-switch"
                @change="onToggleRequests"
              >
              <span class="rp-switch__track" aria-hidden="true"><span class="rp-switch__thumb"></span></span>
              <span class="rp-switch__label" data-test="review-switch-label">{{ switchOn ? 'On' : 'Paused' }}</span>
            </label>
          </div>

          <div v-if="dnsOpen" class="rp-dns" data-test="review-dns-form">
            <div class="rp-dns__t">Add {{ firstName }} to the Do not send list</div>

            <div class="rp-field">
              <label class="sp-label" for="rp-dns-reason">Why?</label>
              <select id="rp-dns-reason" v-model="dnsForm.reason" class="rp-select" data-test="review-dns-reason">
                <option value="" disabled>Pick a reason</option>
                <option v-for="r in reasons" :key="r.value" :value="r.value">{{ r.label }}</option>
              </select>
            </div>

            <div class="rp-field">
              <div class="sp-label">For how long?</div>
              <PillTabs v-model="dnsForm.duration" class="rp-dur" :items="durationTabs" aria-label="For how long?" />
              <input
                v-if="dnsForm.duration === 'until'"
                v-model="dnsForm.untilDate"
                type="date"
                class="sp-input"
                :min="tomorrowYmd"
                aria-label="Pause until"
                data-test="review-dns-until"
              >
            </div>

            <div class="rp-field">
              <div class="rp-label-row">
                <label class="sp-label" for="rp-dns-note">Note for the team <span class="rp-optional">{{ dnsForm.reason === 'other' ? '(required for “Other”)' : '(optional)' }}</span></label>
                <span class="rp-counter" :class="{ 'is-over': dnsForm.note.length > noteMax }" data-test="review-dns-counter">{{ dnsForm.note.length }}/{{ noteMax }}</span>
              </div>
              <textarea
                id="rp-dns-note"
                v-model="dnsForm.note"
                rows="2"
                class="rp-textarea"
                :maxlength="noteMax"
                placeholder="Only staff see this note."
                data-test="review-dns-note"
              ></textarea>
            </div>

            <PanelNotice v-if="dnsError" tone="error" data-test="review-dns-error">{{ dnsError }}</PanelNotice>

            <div class="rp-dns__actions">
              <button type="button" class="sp-btn-ghost" :disabled="dnsSaving" data-test="review-dns-keep" @click="keepSending">Keep sending</button>
              <button type="button" class="sp-btn-primary" :disabled="dnsSaving || !dnsForm.reason" data-test="review-dns-save" @click="saveOptOut">{{ dnsSaving ? 'Saving…' : 'Pause requests' }}</button>
            </div>
          </div>
        </section>

        <section class="sp-sec" data-test="review-history">
          <div class="sp-head">
            <span class="sp-h">History</span>
            <span class="sp-sub rp-count">{{ recentRequests.length }}</span>
          </div>
          <p v-if="!recentRequests.length" class="sp-note" data-test="review-history-empty">No review requests yet for {{ firstName }}.</p>
          <ul v-else class="rp-hist">
            <li v-for="r in recentRequests" :key="r.requestID" class="rp-hist__row" data-test="review-history-row">
              <span class="rp-dot" :class="'is-' + statusMeta(r.status).kind"><span class="rp-dot__d"></span>{{ statusMeta(r.status).label }}</span>
              <span class="rp-hist__meta">{{ r.source === 'manual' ? 'Manual' : 'Auto' }} · {{ formatWhen(r.sentOn || r.createdAt) }}</span>
              <span v-if="r.rating" class="rp-hist__rating"><i class="fa fa-star" aria-hidden="true"></i>{{ r.rating }}</span>
            </li>
          </ul>
        </section>
      </template>
    </div>

    <template v-if="!loading && !loadError" #footer>
      <!-- A confirm, in the panel: the 7-day re-send guard, and taking someone back off the list. -->
      <template v-if="confirm">
        <span class="ap-foot-note" role="alert" data-test="review-confirm">{{ confirmText }}</span>
        <button type="button" class="rp-foot-ghost" data-test="review-confirm-no" @click="answerNo">{{ confirm.kind === 'resend' ? 'Don’t send' : 'Keep paused' }}</button>
        <button type="button" class="rp-foot-btn ap-foot-main" data-test="review-confirm-yes" @click="answerYes">{{ confirm.kind === 'resend' ? 'Send anyway' : 'Turn requests back on' }}</button>
      </template>
      <template v-else>
        <span class="ap-foot-note" :class="{ 'is-warn': footWarn }" data-test="review-foot-note">{{ footNote }}</span>
        <button
          type="button"
          class="rp-foot-btn ap-foot-main"
          :disabled="mainDisabled"
          data-test="review-send-main"
          @click="onSend"
        ><i class="fa fa-star" aria-hidden="true"></i> {{ sending ? 'Sending…' : 'Send review request' }}</button>
      </template>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * Review request — the redesigned panel (design "Patient Action Panels" 2g), opened by
 * PatientPanelActionHost.run('review') through openActionPanel() (480px, 'ap-slideout').
 *
 * `extends` ReviewRequestModal: the status load, the channel rules, the history and — the part that
 * must not drift — the SEND (sendReviewRequest's payload: siteID, patNum, channels, employeeID, force)
 * and the opt-out calls (setReviewPatientOptout) are the classic code. Only the template and the three
 * seams that asked a question with a browser dialog are new:
 *
 *   - SEND FIRST, with its button in the footer (the main button names what it does).
 *   - TURNING REQUESTS OFF asks why INLINE — the reason, how long and a note, validated by
 *     pages/Reviews/doNotSend.js (validateDoNotSendForm) and sent as buildOptOutPayload(form), the
 *     same call the nested DoNotSendModal made — instead of opening that modal.
 *   - CONFIRMS are a row in the panel's footer, never window.confirm: the 7-day re-send guard
 *     (onSend, and doSend's late 409 RECENT_REQUEST fallback) and "Remove from the Do not send list?".
 *     The classic doSend is overridden ONLY for that fallback — its dispatch, error branches and toasts
 *     are the classic ones (tests/component/ReviewPanel.spec.js compares the payloads with the classic).
 *
 * The classic modal has NO mixin.js (a mixin `patNum` computed once replaced its prop — see its
 * header), so its `patNum` / `siteId` / `value` / `patientData` props are inherited as they are and this
 * panel adds none of the shadowed names. `value` is required by the modal; here it defaults to true.
 *
 * ⛔ No `window.confirm` anywhere in this file (patient-action-panels.test.js pins it).
 */
import moment from 'moment-timezone';
import ReviewRequestModal from '@/pages/PatientProfile/components/ReviewRequestModal.vue';
import {
  OPTOUT_REASONS, DURATIONS, NOTE_MAX, validateDoNotSendForm, buildOptOutPayload,
  optOutErrorMessage, reasonLabel, errorForLog,
} from '@/pages/Reviews/doNotSend';
import ActionPanelShell from './ActionPanelShell.vue';
import PanelNotice from './PanelNotice.vue';
import PillTabs from './PillTabs.vue';

function blankForm() {
  return { reason: '', note: '', duration: 'forever', untilDate: '' };
}

export default {
  name: 'ReviewPanel',

  extends: ReviewRequestModal,

  components: { ActionPanelShell, PanelNotice, PillTabs },

  props: {
    // Required on the modal (its b-modal v-model); a panel is simply open.
    value: { type: Boolean, default: true },
  },

  data() {
    return {
      reasons: OPTOUT_REASONS,
      durationTabs: DURATIONS.map(d => ({ id: d.value, label: d.label })),
      noteMax: NOTE_MAX,
      // `dnsOpen` (the modal's flag) now means "the inline form is open".
      dnsForm: blankForm(),
      dnsSaving: false,
      dnsError: '',
      todayYmd: '',
      tomorrowYmd: '',
      /** null | { kind: 'resend', when } | { kind: 'remove' } — the confirm row in the footer. */
      confirm: null,
    };
  },

  computed: {
    firstName() {
      return this.patientFirstName || 'this patient';
    },
    subtitle() {
      const p = this.patientData || {};
      const name = [p.FName, p.LName].filter(Boolean).join(' ').trim();
      return name ? `For ${name}` : '';
    },

    smsOff() {
      return !this.channels.smsAvailable || this.optedOut;
    },
    emailOff() {
      return !this.channels.emailAvailable || this.optedOut;
    },

    /** The switch reads "on" only when requests are on AND the pause form is not open. */
    switchOn() {
      return !this.optedOut && !this.dnsOpen;
    },

    mainDisabled() {
      return this.sendDisabled || this.dnsOpen;
    },
    footNote() {
      if (this.optedOut) return 'Turn requests back on to send';
      if (this.dnsOpen) return 'Finish pausing requests first';
      if (this.repliedStop) return 'Patient replied STOP';
      if (!this.channels.smsAvailable && !this.channels.emailAvailable) return 'No available channel';
      if (!this.anyChannelSelected) return 'Pick at least one channel';
      return '';
    },
    footWarn() {
      return !this.optedOut && !this.dnsOpen && !this.repliedStop && !this.sendHardBlocked && !this.anyChannelSelected;
    },

    confirmText() {
      if (!this.confirm) return '';
      if (this.confirm.kind === 'resend') {
        return `A review request already went to ${this.firstName} (${this.confirm.when}). Send another one anyway?`;
      }
      return `Take ${this.firstName} off the Do not send list?`;
    },
  },

  methods: {
    close() {
      if (this.sending || this.dnsSaving) return;
      this.$emit('closePanel', {});
    },

    // ---- turning requests off / on --------------------------------------------------------

    /** The switch (a native checkbox, kept controlled: it only moves when the state does). */
    onToggleRequests(e) {
      const wantOn = !!(e && e.target && e.target.checked);
      if (e && e.target) e.target.checked = this.switchOn;
      if (this.optToggling || this.dnsSaving) return;
      if (!wantOn) {
        this.openOptOutForm();
        return;
      }
      if (this.dnsOpen) {
        this.keepSending();
        return;
      }
      this.confirm = { kind: 'remove' };
    },

    openOptOutForm() {
      const now = moment.tz(this.timezone || 'America/New_York');
      this.todayYmd = now.format('YYYY-MM-DD');
      this.tomorrowYmd = now.clone().add(1, 'day').format('YYYY-MM-DD');
      this.dnsForm = blankForm();
      this.dnsError = '';
      this.confirm = null;
      this.dnsOpen = true;
    },

    keepSending() {
      this.dnsOpen = false;
      this.dnsError = '';
    },

    /** DoNotSendModal.save(), inline: the same validation, the same payload, the same sentences. */
    async saveOptOut() {
      if (this.dnsSaving) return;
      const err = validateDoNotSendForm(
        { ...this.dnsForm, patNum: this.patNum },
        { todayYmd: this.todayYmd, requirePatient: true },
      );
      if (err) {
        this.dnsError = err;
        return;
      }
      this.dnsSaving = true;
      this.dnsError = '';
      try {
        await this.setReviewPatientOptout({
          siteID: this.siteId,
          patNum: this.patNum,
          employeeID: this.employeeID,
          ...buildOptOutPayload(this.dnsForm),
        });
        this.dnsOpen = false;
        await this.onDoNotSendSaved(); // requestsEnabled = false, the toast, and a status reload
      } catch (e) {
        console.error('[ReviewPanel] opt-out save failed:', errorForLog(e));
        this.dnsError = optOutErrorMessage(e && e.code);
      } finally {
        this.dnsSaving = false;
      }
    },

    // ---- sending ----------------------------------------------------------------------------

    /** The 7-day guard: ask in the footer (the classic asked with window.confirm), then send with force. */
    onSend() {
      if (this.recentWithin7Days) {
        this.confirm = { kind: 'resend', when: this.recentRequestAt ? this.formatWhen(this.recentRequestAt) : 'recently' };
        return;
      }
      this.doSend(false);
    },

    answerYes() {
      const c = this.confirm;
      this.confirm = null;
      if (!c) return;
      if (c.kind === 'resend') this.doSend(true);
      else this.applyOptOut(false, null);
    },

    answerNo() {
      const c = this.confirm;
      this.confirm = null;
      if (c && c.kind === 'resend' && this.$toast) this.$toast.info('No new request sent.');
    },

    /**
     * ReviewRequestModal.doSend with ONE change: its 409 RECENT_REQUEST fallback (a request went out after
     * this opened) asked with window.confirm and retried; here it opens the same footer confirm. The
     * dispatch and every other branch are the classic ones.
     */
    async doSend(force) {
      const selected = [];
      if (this.useSms) selected.push('sms');
      if (this.useEmail) selected.push('email');
      if (!selected.length) {
        this.$toast && this.$toast.warning('Select at least one channel to send.');
        return;
      }

      this.sending = true;
      try {
        await this.sendReviewRequest({
          siteID: this.siteId,
          patNum: this.patNum,
          channels: selected,
          employeeID: this.employeeID,
          force: !!force,
        });
        this.$toast && this.$toast.success('Review request sent.');
        await this.loadStatus();
      } catch (err) {
        console.error('[ReviewPanel] send failed:', errorForLog(err));
        if (err && err.code === 'RECENT_REQUEST' && !force) {
          this.confirm = { kind: 'resend', when: err.lastSentAt ? this.formatWhen(err.lastSentAt) : 'recently' };
        } else if (err && err.code === 'OPTED_OUT') {
          const why = err.reason ? ` (${reasonLabel(err.reason)})` : '';
          this.$toast && this.$toast.error(`This patient is on the Do not send list${why}.`);
          await this.loadStatus();
        } else if (err && err.code === 'REPLIED_STOP') {
          this.$toast && this.$toast.error(err.message || 'This patient replied STOP — no review requests can be sent.');
          await this.loadStatus();
        } else if (err && err.code === 'NO_CHANNEL') {
          this.$toast && this.$toast.error(err.message || 'No available channel to send.');
        } else {
          this.$toast && this.$toast.error((err && err.message) || 'Could not send the review request.');
        }
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.rp-foot-btn { @include ap-btn-primary; }
.rp-foot-ghost { @include ap-btn-ghost; }

.ap-foot-note.is-warn { color: $pp-amber-deep; }

.rp-strong { display: block; font-size: 14px; }
.rp-block { display: block; margin-top: 3px; }
.rp-quote { color: $pp-ink-2; font-weight: 500; }
.rp-count { margin: 0; }

// The banners sit above Send with no divider between them.
.rp-banners { gap: 8px; padding-bottom: 0; }
.rp-banners + .sp-sec { border-top: 0; }

.rp-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin-top: 6px;
  font-size: 12.5px;
}

.rp-chip {
  padding: 1px 8px;
  border: 1px solid $amber-200;
  border-radius: $pp-radius-pill;
  background: $pp-surface;
  font-weight: 700;
}

.sp-sec.is-muted .rp-chan { opacity: 0.7; }

// A channel: the whole card is the checkbox's tap target.
.rp-chan {
  @include ap-row;
  margin: 0;
  padding: 10px 14px;
  border-width: 1.5px;

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; }
  &.is-off { background: $pp-bg-2; cursor: not-allowed; }
}

.rp-chan__box {
  flex: none;
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: $pp-brand-deep;
  cursor: inherit;
}

.rp-chan__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.rp-chan__title { font-size: 14px; font-weight: 700; }
.rp-chan__sub { font-size: 12.5px; color: $pp-ink-3; overflow-wrap: anywhere; }
.rp-chan__sub.is-reason { color: $pp-amber-deep; }
.rp-chan__ic { flex: none; color: $pp-ink-4; }

// Automatic requests.
.rp-auto { display: flex; align-items: flex-start; gap: 16px; }
.rp-auto__text { flex: 1 1 auto; min-width: 0; }

.rp-switch {
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;

  @include ap-touch { min-height: 44px; }
}

.rp-switch__input {
  position: absolute;
  inset: 0;
  z-index: 2;
  margin: 0;
  opacity: 0;
  cursor: pointer;

  &:disabled { cursor: not-allowed; }
  &:focus-visible ~ .rp-switch__track { outline: 2px solid $pp-brand; outline-offset: 2px; }
}

.rp-switch__track {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: $neutral-300;
  transition: background 0.15s ease;
}

.rp-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease;
}

.rp-switch__input:checked ~ .rp-switch__track { background: $pp-brand-deep; }
.rp-switch__input:checked ~ .rp-switch__track .rp-switch__thumb { transform: translateX(18px); }
.rp-switch__label { min-width: 44px; font-size: 13px; font-weight: 700; color: $pp-ink-2; }

// The inline "why?" form.
.rp-dns {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-bg-2;
}

.rp-dns__t { font-size: 14px; font-weight: 700; }
.rp-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.rp-select { @include ap-select; }
.rp-textarea { @include ap-textarea; min-height: 72px; }
.rp-optional { font-weight: 500; color: $pp-ink-4; letter-spacing: 0; }
.rp-label-row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.rp-counter { font-size: 12px; color: $pp-ink-4; }
.rp-counter.is-over { color: $pp-rose-ink; font-weight: 700; }

.rp-dns ::v-deep .rp-dur { align-self: stretch; flex-wrap: wrap; }
.rp-dns ::v-deep .rp-dur .pt__tab { flex: 1 1 auto; justify-content: center; padding: 0 10px; }

.rp-dns__actions { display: flex; justify-content: flex-end; gap: 8px; }

// History.
.rp-hist { margin: 0; padding: 0; list-style: none; }

.rp-hist__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;

  & + & { border-top: 1px solid $pp-line-2; }
}

.rp-hist__meta { margin-left: auto; font-size: 12.5px; color: $pp-ink-3; white-space: nowrap; }
.rp-hist__rating { display: inline-flex; align-items: center; gap: 3px; min-width: 30px; font-size: 12.5px; font-weight: 700; color: $pp-amber-deep; }

.rp-dot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: $pp-ink-2;
}

.rp-dot__d { width: 8px; height: 8px; border-radius: 50%; background: $neutral-400; }
.rp-dot.is-blue .rp-dot__d { background: $pp-blue-ink; }
.rp-dot.is-teal .rp-dot__d { background: $pp-brand; }
.rp-dot.is-green .rp-dot__d { background: $pp-green-ink; }
.rp-dot.is-amber .rp-dot__d { background: $amber-500; }
.rp-dot.is-red .rp-dot__d { background: $pp-red; }
.rp-dot.is-gray .rp-dot__d { background: $neutral-300; }

@include ap-phone {
  .rp-hist__row { flex-wrap: wrap; }
  .rp-hist__meta { white-space: normal; }
}
</style>
